import { ref, watch, computed } from "vue";
import type { Ref } from "vue";
import type {
  MaybeRef,
  MaybeRefObjectMaybeProperty,
  SureRef,
} from "@toolsie/composables";
import type { UseFetchOptions, UseFetchOutput } from "@/typings/useFetch";
import { getProperty, useToggle } from "@toolsie/composables";

const defaultOptions = {
  noAuth: false,
  contentType: "application/json",
  returnType: "json",
  method: "GET",
  body: null,
  immediate: false,
  refetch: false,
};

/**
 * @function useFetch - composable function to use fetch in a non async vue way
 * @param {SureRef<url>} url - the url to call
 * @param {MaybeRef<UseFetchOptions>} options - options that determine the behavior
 * @returns {UseFetchOutput} - data and functions to manipulate the fetch function
 */
export default function useFetch(
  url: SureRef<string>,
  options?: MaybeRef<UseFetchOptions>
): UseFetchOutput {
  /** @const {Ref<boolean>} error - the request was unsuccessful */
  const error = ref(false);
  /** @const {Ref<string>} errorMessage - The error message obtained form the request */
  const errorMessage = ref("");
  /** @const {Ref<boolean>} fetching - the request is fetching unsuccessful */
  const fetching = ref(false);
  /** @const {Ref<unknown>} response - response of the request*/
  const response: Ref<unknown> = ref();

  /** @const {Ref<number>} status - status code of the response of the request*/
  const status: Ref<number> = ref(0);

  /**  @const {Ref<string>} theUrl - The url we're going to use */
  const theUrl = ref(url);

  //value that trigger the fetch and the trigger function
  const { value: trigger, toggle: start } = useToggle();

  //abortion controls

  const controller = new AbortController();
  const signal = controller.signal;

  const { value: abort } = useToggle();

  watch(abort, () => {
    controller.abort();
  });

  //options obtained

  const contentType = getProperty(
    options as MaybeRefObjectMaybeProperty<string>,
    "contentType",
    defaultOptions.contentType
  );
  const returnType = getProperty(
    options as MaybeRefObjectMaybeProperty<string>,
    "returnType",
    defaultOptions.returnType
  );
  const method = getProperty(
    options as MaybeRefObjectMaybeProperty<string>,
    "method",
    defaultOptions.method
  );
  const obtainedBody = getProperty(
    options as MaybeRefObjectMaybeProperty<unknown>,
    "body",
    defaultOptions.body
  );
  const immediate = getProperty(
    options as MaybeRefObjectMaybeProperty<boolean>,
    "immediate",
    defaultOptions.immediate
  );
  const refetch = getProperty(
    options as MaybeRefObjectMaybeProperty<boolean>,
    "refetch",
    defaultOptions.refetch
  );

  //this is the body that is going to be delivered, must be computed because it needs to be transformed
  const body: Ref<unknown> = computed(() => {
    if (contentType.value == "application/json" && obtainedBody.value != null) {
      return JSON.stringify(obtainedBody);
    } else {
      return null;
    }
  });

  //the function that actually do the fetch
  const doFetch = async () => {
    try {
      //restart the information flags
      error.value = false;
      errorMessage.value = "";
      fetching.value = true;
      status.value = 0;
      //do the fetch
      const content: Response = await fetch(theUrl.value, {
        signal,
        method: method.value,
        body: body.value as BodyInit,
      });
      //if the content is wrong throw the error with the message
      if (!content.ok) {
        error.value = true;
        fetching.value = false;
        status.value = content.status;
        const jsonContent = await content.json();
        errorMessage.value = jsonContent.message;
        return;
      }

      //if the content obtained is ok and the possible answer is a json convert it to json
      if (returnType.value == "json") {
        const jsonContent = await content.json();
        fetching.value = false;
        response.value = jsonContent;
        status.value = content.status;
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      error.value = true;
      errorMessage.value = err.message;
      fetching.value = false;
    }
  };

  watch(trigger, () => {
    doFetch();
  });

  //refetch in case the option is set
  if (refetch.value) {
    watch(theUrl, () => {
      start();
    });
  }

  //run if immediate is set
  if (immediate.value) {
    start();
  }

  return { error, errorMessage, fetching, status, start, response };
}
